/**
 * The XMP packet — `readPacket` ported from the DI-T1 spike (`jpeg/xmp.ts`),
 * plus a small XML tokenizer that turns the packet into properties.
 *
 * Measured on the real sets (T1 `py/survey.py`, 2026-09-18): the P1 packet is
 * 8 159 B with ~6 KB trailing padding, the Sony packet 3 037–3 048 B with
 * 2 049 B padding; every file ends with `<?xpacket end="w"?>`. The P1 writes
 * all of its `drone-dji` keys as attributes of one `rdf:Description`; other
 * DJI firmwares and XMP writers use child elements — both forms are read. A
 * regex over the whole packet (the spike's reader) would miss one of them or
 * match inside a value, hence the tokenizer.
 *
 * Output: every property of the `drone-dji` namespace as `localName → text`
 * (a string until the boundary — `RelativeAltitude: "0.189"`), and every
 * property of every other namespace as `prefix:localName → text`. The
 * namespace is resolved through the `xmlns:` declarations, not guessed from
 * the prefix.
 */

import { XMP_SIG } from "./segments.js";

// Namespace identifiers as the files carry them — not fetched, so not https.
// eslint-disable-next-line no-restricted-syntax
export const DRONE_DJI_NS = "http://www.dji.com/drone-dji/1.0/";
// eslint-disable-next-line no-restricted-syntax
const RDF_NS = "http://www.w3.org/1999/02/22-rdf-syntax-ns#";

export interface XmpPacket {
  /** the packet text, i.e. the APP1 payload after the namespace signature */
  text: string;
  /** byte offset of the packet inside the buffer */
  fileOffset: number;
  byteLength: number;
  /** trailing whitespace between </x:xmpmeta> and <?xpacket end=...?> */
  paddingBytes: number;
  /** true when the packet declares itself rewritable */
  writable: boolean;
}

const dec = new TextDecoder("utf-8");

export function readPacket(
  buf: Uint8Array,
  payloadStart: number,
  payloadEnd: number,
): XmpPacket {
  const start = payloadStart + XMP_SIG.length;
  const bytes = buf.subarray(start, payloadEnd);
  const text = dec.decode(bytes);
  const close = text.lastIndexOf("</x:xmpmeta>");
  const endPi = text.lastIndexOf("<?xpacket end=");
  let padding = 0;
  if (close >= 0 && endPi > close) {
    const between = text.slice(close + "</x:xmpmeta>".length, endPi);
    padding = between.length - between.trim().length;
  }
  return {
    text,
    fileOffset: start,
    byteLength: bytes.length,
    paddingBytes: padding,
    writable: /<\?xpacket end=['"]w['"]\?>/.test(
      text.slice(endPi < 0 ? 0 : endPi),
    ),
  };
}

export interface XmpProperties {
  /** `drone-dji` local name → text */
  droneDji: Record<string, string>;
  /** `<prefix>:<localName>` → text, every other namespace */
  other: Record<string, string>;
}

function decodeEntities(s: string): string {
  if (!s.includes("&")) return s;
  return s.replace(
    /&(#x[0-9a-fA-F]+|#[0-9]+|amp|lt|gt|quot|apos);/g,
    (_, e: string) => {
      switch (e) {
        case "amp":
          return "&";
        case "lt":
          return "<";
        case "gt":
          return ">";
        case "quot":
          return '"';
        case "apos":
          return "'";
        default:
          return String.fromCodePoint(
            e.startsWith("#x")
              ? parseInt(e.slice(2), 16)
              : parseInt(e.slice(1), 10),
          );
      }
    },
  );
}

interface Attr {
  name: string;
  value: string;
}

/** Parses the attributes of one start tag body (`name a="1" b='2'`). */
function parseAttributes(body: string): Attr[] {
  const out: Attr[] = [];
  const re = /([A-Za-z_][\w.:-]*)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    out.push({ name: m[1] ?? "", value: decodeEntities(m[2] ?? m[3] ?? "") });
  }
  return out;
}

interface Frame {
  qname: string;
  /** namespace declarations in scope for this element */
  ns: Map<string, string>;
  /** collected character data */
  text: string;
  /** `rdf:li` values collected under this element */
  items: string[];
  /** true when the element had element children (a struct / container) */
  hasChildren: boolean;
}

/**
 * Tokenizes the packet: processing instructions and comments are skipped,
 * start / end / empty tags are matched on a stack, character data is kept.
 */
export function parseXmpProperties(text: string): XmpProperties {
  const droneDji: Record<string, string> = {};
  const other: Record<string, string> = {};
  const stack: Frame[] = [];

  const resolve = (qname: string, ns: Map<string, string>) => {
    const colon = qname.indexOf(":");
    if (colon < 0) return { uri: "", prefix: "", local: qname };
    const prefix = qname.slice(0, colon);
    return { uri: ns.get(prefix) ?? "", prefix, local: qname.slice(colon + 1) };
  };

  const emit = (qname: string, value: string, ns: Map<string, string>) => {
    const { uri, prefix, local } = resolve(qname, ns);
    if (uri === RDF_NS || prefix === "xml") return;
    if (uri === DRONE_DJI_NS) {
      if (!(local in droneDji)) droneDji[local] = value;
    } else {
      const key = `${prefix}:${local}`;
      if (!(key in other)) other[key] = value;
    }
  };

  let i = 0;
  while (i < text.length) {
    const lt = text.indexOf("<", i);
    if (lt < 0) break;
    if (lt > i && stack.length > 0) {
      const top = stack[stack.length - 1];
      if (top) top.text += text.slice(i, lt);
    }
    if (text.startsWith("<?", lt)) {
      const end = text.indexOf("?>", lt + 2);
      i = end < 0 ? text.length : end + 2;
      continue;
    }
    if (text.startsWith("<!--", lt)) {
      const end = text.indexOf("-->", lt + 4);
      i = end < 0 ? text.length : end + 3;
      continue;
    }
    const gt = text.indexOf(">", lt);
    if (gt < 0) break;
    const raw = text.slice(lt + 1, gt);
    i = gt + 1;

    if (raw.startsWith("/")) {
      // end tag
      const frame = stack.pop();
      if (!frame) continue;
      const parent = stack[stack.length - 1];
      if (parent) parent.hasChildren = true;
      const { uri, local } = resolve(frame.qname, frame.ns);
      if (uri === RDF_NS && local === "li") {
        parent?.items.push(decodeEntities(frame.text.trim()));
        continue;
      }
      if (uri === RDF_NS) {
        // rdf:Seq / rdf:Bag / rdf:Alt hand their items up to the property
        if (frame.items.length > 0) parent?.items.push(...frame.items);
        continue;
      }
      // x:xmpmeta and other wrappers: only a leaf element is a property
      if (!parent) continue;
      // a property element: its value is its text, or its list items
      if (frame.items.length > 0)
        emit(frame.qname, frame.items.join("; "), frame.ns);
      else if (!frame.hasChildren)
        emit(frame.qname, decodeEntities(frame.text.trim()), frame.ns);
      continue;
    }

    const selfClosing = raw.endsWith("/");
    const body = selfClosing ? raw.slice(0, -1) : raw;
    const nameEnd = body.search(/[\s/]|$/);
    const qname = body.slice(0, nameEnd);
    const attrs = parseAttributes(body.slice(nameEnd));
    const parentNs = stack[stack.length - 1]?.ns;
    const ns = new Map(parentNs ?? []);
    for (const a of attrs) {
      if (a.name.startsWith("xmlns:")) ns.set(a.name.slice(6), a.value);
    }
    // attributes are properties (rdf:Description a="…", and x:xmpmeta x:xmptk)
    for (const a of attrs) {
      if (a.name.startsWith("xmlns:") || a.name === "xmlns") continue;
      emit(a.name, a.value, ns);
    }
    if (!selfClosing) {
      stack.push({ qname, ns, text: "", items: [], hasChildren: false });
    } else {
      const parent = stack[stack.length - 1];
      if (parent) parent.hasChildren = true;
    }
  }
  return { droneDji, other };
}
